/**
 * CopyCampaignWorkflow — top-level container for the "copy campaign" flow
 * (Flow A). Owns the 3-step state machine (1 Describe → 2 Confirm & adjust → 3 Push)
 * and the shared draft, and renders the step whose component matches `step`.
 *
 * Mounted as a full route (`/campaign/copy`), matching this app's existing
 * convention for multi-step campaign flows (see App.js's `/campaign/v3/start`
 * -> CampaignLaunchWorkflow) rather than a modal overlay — CampaignLaunchWorkflow.css's
 * own doc comment confirms these flows render inline inside NewLayout's main
 * area, and copy-workflow.css's EMBEDDING ADAPTATION note follows the same
 * precedent (height: 100%, no fullscreen overlay).
 *
 * Entry point ids arrive as `?campaignIds=1,2,3` on the route (see the
 * onStartCopyCampaign wiring added to App.js). If no ids are present — the
 * "Copy campaign" toolbar button on CampaignsPage passes `[]` deliberately,
 * meaning "let me pick inside the workflow" — CopyCampaignEntryPicker.js
 * renders an in-workflow campaign browser (Gap #4) instead of the old
 * dead-end "go back and select campaigns first" message; on confirming a
 * selection there it feeds the chosen ids into this SAME `?campaignIds=` URL
 * param (see the setSearchParams call below) rather than a second parallel
 * "selected campaigns" state path, so nothing downstream needs to know
 * whether campaigns arrived via URL preselection or in-workflow picking.
 *
 * BUILD-THEN-REVIEW: leaving step 1 fires one POST /api/v2/campaigns/copy
 * with planningMode per source campaign — the backend BUILDS the real
 * launcher template and pauses at orchestration status 'review' instead of
 * launching. Step 2 (Confirm & adjust) renders/edits that template through
 * GET/PUT /planning-review; step 3 fires POST /planning-launch per build. See
 * the buildsByCampaignId state below for the per-build shape.
 *
 * The separate "Confirm what's copied" picker step (CopyPickerStep + the
 * per-campaign PREVIEW import/poll machinery and its excludedIds subtractive
 * selection, behind the enable-copy-campaign-picker-step account setting) is
 * GONE — confirming and adjusting are ONE step. The built plan already lists
 * every ad group / line item the copy will create, so dropping one is a row
 * DELETION in that grid: it edits the REAL built template through the same
 * planning-review PUT every other edit takes, instead of pre-filtering ids
 * before the build and then making the user read the same list twice. It also
 * removed an entire duplicate import round-trip per campaign.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { getUserData } from '../../utils/userUtils';
import CopyCampaignEntryPicker from './CopyCampaignEntryPicker';
import CopyDescribeStep from './CopyDescribeStep';
import CopyPlanStep from './CopyPlanStep';
import CopyPushStep from './CopyPushStep';
import {
  fetchSourceCampaigns, interpretCopyInstruction, normalizeCopyPlatform,
  fetchExistingCampaignNames, computeUniqueCopyRename, defaultCopyCampaignName,
  normalizeCampaignNameForCompare,
  fetchMessageStatus, TERMINAL_MESSAGE_STATUSES,
  isGoogleAdsCopyPlatform,
  copyCampaign, fetchPlanningReview, savePlanningReview, isBuildReady, resolveGates,
  deleteCampaignRow, SHOW_COPY_INTERPRET_UI,
  copyStepLabels,
} from './copyWorkflowApi';
import { campaignNamesFromLauncherResult } from './copyReviewEdits';
import useUnsavedChangesGuard, { copyLeaveMessage } from './useUnsavedChangesGuard';
import { geoOverrideIncluded } from './CopyGeoSuggest';
import './copy-workflow.css';

// Poll interval for the per-campaign build messages below — matches
// CopyPushStep.js's `pollOne` interval exactly (same backend action family,
// same acceptable staleness).
const BUILD_POLL_INTERVAL_MS = 5000;

// Parse the DV360 geo-override textareas: ONE location name per line (names carry commas —
// "Chicago, Illinois, United States" — so lines, not commas, are the separator). Trims and
// drops blanks.
const parseGeoLines = (text) => String(text || '')
  .split('\n')
  .map((s) => s.trim())
  .filter(Boolean);

export default function CopyCampaignWorkflow() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();
  const userId = user?.userId || user?.id;

  // ── Step list ───────────────────────────────────────────────────────────
  // One shape for every account: Set up → Confirm & adjust → Push. There is no
  // longer an account setting that inserts a fourth step (see copyStepLabels).
  const stepLabels = useMemo(() => copyStepLabels(), []);
  // Same order as stepLabels, so index i of one names step i of the other.
  const activeSteps = useMemo(() => ['describe', 'plan', 'push'], []);


  // SINGLE SELECT: the flow copies ONE campaign per run. Both entry points
  // now select one, but a stale/hand-edited ?campaignIds= with several would
  // otherwise build them all against the FIRST campaign's platform (the
  // payload carries one `platform` for the batch) — silently wrong for a
  // mixed Search + PMax selection. Take the first and ignore the rest.
  const campaignIds = useMemo(() => {
    const raw = searchParams.get('campaignIds') || '';
    const all = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (all.length > 1) {
      console.warn(`Copy campaign takes one campaign at a time — using ${all[0]}, ignoring ${all.length - 1} more.`);
    }
    return all.slice(0, 1);
  }, [searchParams]);

  // Step identity is a KEY, not a number — kept from when an account setting
  // could insert a fourth step and shift every index under it (that shift once
  // left Adjust showing "1 still building" forever, because CopyPlanStep reads
  // a campaign with no build entry as building and the navigation that calls
  // startBuilds() had been bypassed). The flow is one fixed shape now, but
  // addressing steps by name is still what keeps the rail and the rendered
  // component from ever disagreeing.
  //
  // The step lives in the URL rather than component state. Everything that
  // leaves this component used to lose it: a refresh, the browser Back
  // button, and — the case that stranded people — a planning-mode build that
  // outlived its tab. The build creates a REAL campaigns row at step-2 entry,
  // so the copy exists and is launchable, but the only way back to its Push
  // step was to start the flow over (which builds a second row). ?step= makes
  // the step addressable, and the campaigns list links straight to it.
  const step = useMemo(() => {
    const fromUrl = searchParams.get('step');
    return activeSteps.includes(fromUrl) ? fromUrl : 'describe';
  }, [searchParams, activeSteps]);

  // Takes a value or an updater, exactly like the useState setter it replaces
  // (goBack passes a function).
  //
  // REPLACES the history entry rather than pushing one. Pushing would make the
  // browser's Back button walk the steps, which sounds like a bonus but breaks
  // useUnsavedChangesGuard: it guards Back with a sentinel entry + popstate,
  // and on "leave" calls history.back() — which with per-step entries lands on
  // the previous STEP while having just told the user they'd have to start the
  // copy again. Back therefore keeps its current meaning (leave the flow, with
  // the guard's prompt), and stepping stays the rail's own Back button.
  const setStep = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(step) : next;
    if (resolved === step) return;
    const params = new URLSearchParams(searchParams);
    params.set('step', resolved);
    setSearchParams(params, { replace: true });
  }, [step, searchParams, setSearchParams]);

  // ── Source campaigns ─────────────────────────────────────────────────────

  const [sourceCampaigns, setSourceCampaigns] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState(null);

  useEffect(() => {
    if (campaignIds.length === 0) return;
    let cancelled = false;
    setSourcesLoading(true);
    setSourcesError(null);
    fetchSourceCampaigns(campaignIds, { accountId, userId })
      .then((campaigns) => { if (!cancelled) setSourceCampaigns(campaigns); })
      .catch((err) => { if (!cancelled) setSourcesError(err.response?.data?.error || err.message || 'Failed to load campaigns'); })
      .finally(() => { if (!cancelled) setSourcesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignIds.join(',')]);

  const platform = useMemo(() => {
    const first = sourceCampaigns[0];
    const resolved = first ? normalizeCopyPlatform(first.destination) : null;
    // TEMP DIAGNOSTIC (remove after debugging the DV360 geo field): shows the raw
    // destination slug and what it normalized to. The DV360-only "Geos" field renders
    // only when this logs `platform=dv360`.
    // eslint-disable-next-line no-console
    console.log('[copy-workflow] source destination=', first?.destination, '-> platform=', resolved);
    return resolved;
  }, [sourceCampaigns]);

  // ── Step 1 state — describe ──────────────────────────────────────────────
  const [prompt, setPrompt] = useState('');
  const [intent, setIntent] = useState('new_market');
  const [interpretation, setInterpretation] = useState(null);
  const [interpreting, setInterpreting] = useState(false);
  const [opOverrides, setOpOverrides] = useState({});
  // Per-campaign rename overrides from CopyDescribeStep's inline-editable
  // per-campaign name (one per sourceCampaigns entry, keyed by campaign id).
  // Lifted here because it feeds the build payload's newName
  // (buildPayloadFor below, precedence: override > `${name} (copy)`), and the
  // duplicate-name quick-fix writes back into it so a REBUILD keeps the
  // fixed name.
  const [renameOverrides, setRenameOverrides] = useState({});
  // The PRE-FILLED campaign name per source campaign id — `<source name> <date> <time>`,
  // computed ONCE when the source loads and then frozen (see the effect below).
  //
  // Deliberately NOT seeded into renameOverrides: that state means "the user chose a
  // name", it is what the duplicate-name quick-fix writes into, and hasUnsavedEdits reads
  // it to decide whether backing out of step 1 should warn. Seeding it would make an
  // untouched form look edited. The name field shows this as its value and only writes to
  // renameOverrides when the user actually types, and buildPayloadFor falls back to it.
  const [nameDefaults, setNameDefaults] = useState({});
  const [formFields, setFormFields] = useState({ startDate: '', endDate: '', budget: '', budgetType: 'daily' });
  // DV360-only geo override (one location name per line). Campaign-wide: replaces the source
  // GEO_REGION targeting on every copied line item. Ignored/never rendered for TTD / Google Ads
  // Search / PMax, whose copy launchers have no such input (see buildPayloadFor's platform gate).
  const [dv360Geo, setDv360Geo] = useState({ included: '', excluded: '' });
  // Google Ads (Search + PMax) geo override: `picked` Google suggestions ([{ resourceName,
  // label }]), `included` typed names, one per line, and `pending` search-box text (see
  // CopyGeoSuggest). Gated to the two Google platforms
  // in buildPayloadFor + the render; both blank = keep the source geo.
  const [searchGeo, setSearchGeo] = useState({ included: '', picked: [] });
  // DV360-only target advertiser id: create the copy under a DIFFERENT advertiser than the source.
  // Blank = keep the source advertiser (ordinary same-advertiser copy). Gated to platform ===
  // 'dv360' in buildPayloadFor + the render; a different advertiser drops advertiser-scoped
  // creatives/audiences (they can't carry across advertisers).
  const [targetAdvertiserId, setTargetAdvertiserId] = useState('');
  // GOOGLE ADS destination account. Google Ads has no "advertiser" — the account a
  // campaign lives in is a CUSTOMER id — so this is targetCustomerId internally while
  // step 1 labels it "Advertiser ID", which is the word the people running these copies
  // use. Two pieces of state rather than one so that clearing the box does not silently
  // re-point the copy at the source: the CHECKBOX is what decides, and it defaults to
  // checked (same account), which is the overwhelmingly common case and the behaviour
  // every copy had before this field existed.
  const [copyToSameAccount, setCopyToSameAccount] = useState(true);
  const [targetCustomerId, setTargetCustomerId] = useState('');
  // Google Ads (Search + PMax) step-1 override: target CPA. It is optional and an
  // untouched box is omitted from the payload. Search interprets omission as keeping
  // the source bidding; PMax deliberately maps a source target-CPA strategy to plain
  // Maximize Conversions without copying its numeric target.
  // Google Ads (Search + PMax): TARGET CPA only. Every URL-tracking control that
  // used to live here — the campaign's template and suffix, the two
  // apply-to-every-sitelink boxes, and the "don't carry over the source's
  // templates" switch — moved to step 2's URL panel, where the fields and the URL
  // they assemble are on screen together. Step 1 runs before the build, so it
  // could show neither. Target CPA stays because it is bidding, not tracking.
  const [gadsOverrides, setGadsOverrides] = useState({ targetCpa: '' });
  const [manualOpen, setManualOpen] = useState(false);

  // ── Step-1 draft ─────────────────────────────────────────────────────────
  // The URL carries WHERE you are; this carries WHAT YOU TYPED. These inputs
  // are too bulky for a query string and are nobody else's business, so they
  // go to sessionStorage — per tab, cleared by the browser on close. Keyed by
  // the source campaign so copying two campaigns in two tabs keeps two drafts.
  // Without it, resuming at Push showed the plan but an empty changeset
  // summary: Budget, Flight and the applied/declined ops all read step-1 state.
  const draftKey = campaignIds.length ? `copy-draft:${campaignIds.join(',')}` : null;
  // Gates the writer below: it must not run until the restore has been applied,
  // or the first commit would overwrite a stored draft with empty initial
  // state. Flipping it via state (not a ref) means the writer re-runs on the
  // commit AFTER the restored values land, so it saves them, not the blanks.
  const [draftReady, setDraftReady] = useState(false);
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    try {
      const stored = sessionStorage.getItem(draftKey);
      if (stored) {
        const draft = JSON.parse(stored);
        if (draft.prompt) setPrompt(draft.prompt);
        if (draft.intent) setIntent(draft.intent);
        if (draft.interpretation) setInterpretation(draft.interpretation);
        if (draft.opOverrides) setOpOverrides(draft.opOverrides);
        if (draft.renameOverrides) setRenameOverrides(draft.renameOverrides);
        if (draft.nameDefaults) setNameDefaults(draft.nameDefaults);
        if (draft.formFields) setFormFields((prev) => ({ ...prev, ...draft.formFields }));
        if (draft.dv360Geo) setDv360Geo(draft.dv360Geo);
        if (draft.searchGeo) setSearchGeo(draft.searchGeo);
        if (draft.targetAdvertiserId) setTargetAdvertiserId(draft.targetAdvertiserId);
        // Restored only when explicitly false: a draft written before this field
        // existed has no key, and must keep defaulting to the same-account copy.
        if (draft.copyToSameAccount === false) setCopyToSameAccount(false);
        if (draft.targetCustomerId) setTargetCustomerId(draft.targetCustomerId);
        // Merged, not replaced: a draft written before a field existed would otherwise
        // drop the new key and leave its input reading `undefined`.
        if (draft.gadsOverrides) setGadsOverrides((prev) => ({ ...prev, ...draft.gadsOverrides }));
      }
    } catch (err) {
      // Corrupt JSON, or storage blocked (private mode). Starting from a blank
      // step 1 is the correct degradation — never block the flow on a draft.
      console.warn('Could not restore the copy draft — starting fresh', err);
    } finally {
      setDraftReady(true);
    }
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey || !draftReady) return;
    try {
      sessionStorage.setItem(draftKey, JSON.stringify({
        prompt, intent, interpretation, opOverrides, renameOverrides, nameDefaults,
        formFields, dv360Geo, searchGeo, targetAdvertiserId, gadsOverrides,
        copyToSameAccount, targetCustomerId,
      }));
    } catch (err) {
      // Quota or a storage-less context: the flow still works, it just stops
      // being resumable. Not worth interrupting the user over.
      console.warn('Could not save the copy draft', err);
    }
  }, [draftKey, draftReady, prompt, intent, interpretation, opOverrides,
      renameOverrides, nameDefaults, formFields, dv360Geo, searchGeo, targetAdvertiserId,
      gadsOverrides, copyToSameAccount, targetCustomerId]);

  // Freeze the pre-filled name once per source campaign. Gated on draftReady so a
  // restored draft's own frozen names win: re-deriving them on resume would move the
  // timestamp forward and quietly rename a copy the user had already named.
  //
  // `new Date()` is read HERE, once per campaign, rather than inside the render — the
  // name records when the copy was started, and a value recomputed on every render would
  // change under the user as the clock ticked past a minute boundary.
  useEffect(() => {
    if (!draftReady || sourceCampaigns.length === 0) return;
    setNameDefaults((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const c of sourceCampaigns) {
        if (!c?.id || next[c.id]) continue;
        next[c.id] = defaultCopyCampaignName(c.name, new Date());
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [draftReady, sourceCampaigns]);

  const handleReRead = async () => {
    setInterpreting(true);
    try {
      const result = await interpretCopyInstruction(prompt, sourceCampaigns, formFields, { accountId, userId });
      if (result.error) {
        // interpret_copy_instruction's own clean "LLM unavailable" fallback —
        // a complete, valid 'completed'-status result, not a request failure
        // (see resolveCampaignCopyIntentStatus in messageStatus.js). `ops`/
        // `intent` are always empty/null on this path, so leave interpretation
        // null rather than storing a 0-ops result — CopyDescribeStep's own
        // "Describe the copy above and hit Re-read..." empty state (which
        // doubles as the manual-parameters nudge) then reads correctly
        // instead of silently rendering an unexplained empty op list.
        setInterpretation(null);
        alert(`Could not interpret that description (${result.error}) — set the parameters manually instead.`);
      } else {
        setInterpretation(result);
        if (result.intent) setIntent(result.intent);
        // The interpreter parses start/end/budget deterministically from the
        // instruction + form context (interpretation.fields — never LLM-
        // invented, see copy_intent_reasoning._build_fields). Pre-fill the
        // form with them so "start mid-August, double the budget" doesn't
        // have to be re-typed — but NEVER overwrite a value the user already
        // entered by hand: their explicit input outranks the parse.
        const parsed = result.fields || {};
        setFormFields((prev) => ({
          ...prev,
          ...(!prev.startDate && parsed.start && { startDate: parsed.start }),
          ...(!prev.endDate && parsed.end && { endDate: parsed.end }),
          ...(!prev.budget && parsed.budget != null && parsed.budget !== ''
            && { budget: String(parsed.budget) }),
          ...(parsed.budget_type && prev.budgetType === 'daily'
            && { budgetType: parsed.budget_type }),
        }));
      }
    } catch (err) {
      console.error('Error interpreting copy instruction', err);
      alert('Could not interpret that description — set the parameters manually instead.');
    } finally {
      setInterpreting(false);
    }
  };

  // ── Step 2 state — planning-mode builds ──────────────────────────────────
  // buildsByCampaignId: { [sourceCampaign.id]: {
  //   status: 'building' | 'review' | 'failed',
  //   campaignRowId: the NEW campaigns-table row POST /copy created (what
  //                  planning-review/-launch are addressed to),
  //   messageId, chatId, links: from POST /copy's response,
  //   error: string | null (set only once status === 'failed'),
  //   launcherResult: the REAL built copy template from GET /planning-review
  //                   (with targeting_plan/creative_plan/gates riding on it),
  //   reviewPlatform, launcherResultPath, alreadyLaunched: from the same GET,
  //   saving: bool — a savePlanningReview PUT is in flight,
  //   saveError: string | null — the last PUT's failure, if any,
  //   payloadKey: JSON of the POST /copy payload this build was started with,
  //               so re-entering step 2 after changing step-1 inputs (rename,
  //               dates, budget, accepted ops) rebuilds instead of showing a
  //               plan for inputs the user has since changed.
  // } }
  // One build per source campaign, kicked off concurrently when the user
  // leaves step 1 (see goToAdjust/startBuilds below). Gates arrive on
  // launcherResult.gates from the backend's copy_gates.py run against the
  // real template.
  const [builds, setBuilds] = useState({});
  const buildPollTimers = useRef([]);
  useEffect(() => () => { buildPollTimers.current.forEach((t) => clearInterval(t)); }, []);
  // Ref mirrors so startBuildFor's superseded-draft cleanup can read the
  // build being REPLACED without taking `builds`/`pushed` as dependencies
  // (which would re-create the callback chain on every poll tick).
  // pushedRef is synced next to the `pushed` state declaration below.
  const buildsRef = useRef(builds);
  useEffect(() => { buildsRef.current = builds; }, [builds]);
  const pushedRef = useRef(false);

  const patchBuild = useCallback((campaignId, patch) => {
    setBuilds((prev) => ({ ...prev, [campaignId]: { ...(prev[campaignId] || {}), ...patch } }));
  }, []);

  // ── Resuming a build ─────────────────────────────────────────────────────
  // The flow copies ONE campaign per run, so there is at most one build and
  // ?build= can name its campaigns-row id outright.
  const primaryBuildRowId = builds[campaignIds[0]]?.campaignRowId || null;

  // Mirror the row id into the URL as soon as the build has one. Written with
  // replace so mid-build polling doesn't stack history entries — only the
  // step transitions above are meant to be Back-able.
  useEffect(() => {
    if (!primaryBuildRowId || searchParams.get('build') === primaryBuildRowId) return;
    const params = new URLSearchParams(searchParams);
    params.set('build', primaryBuildRowId);
    setSearchParams(params, { replace: true });
  }, [primaryBuildRowId, searchParams, setSearchParams]);

  // Adopt a build this session didn't create — a refresh, or arriving from the
  // campaigns list, where a planning-mode copy row links back to its own Push
  // step. Reads the SAVED template through planning-review and NEVER starts a
  // build: a POST /copy here would create a second campaigns row for a copy
  // that already exists, which is the pile-up this whole change exists to stop.
  const hydratedBuildRef = useRef(null);
  useEffect(() => {
    const buildRowId = searchParams.get('build');
    const sourceId = campaignIds[0];
    if (!buildRowId || !sourceId) return;
    if (hydratedBuildRef.current === buildRowId) return;
    if (buildsRef.current?.[sourceId]?.campaignRowId === buildRowId) return;
    hydratedBuildRef.current = buildRowId;

    let cancelled = false;
    patchBuild(sourceId, { status: 'building', campaignRowId: buildRowId, error: null });
    fetchPlanningReview(buildRowId, { accountId, userId })
      .then((review) => {
        if (cancelled) return;
        // Already pushed: the copy is a real campaign now, not a draft to
        // finish. Resuming onto the Push step would show a launched plan with
        // nothing left to launch, so hand the user the campaign instead. The
        // campaigns list makes the same call before routing here; this covers
        // everything that bypasses it — a bookmarked ?build= URL, a copy
        // launched in another tab, a list row read before the launch landed.
        //
        // review.launched, NOT review.alreadyLaunched: that flag only means
        // "no longer awaiting review" and is true of an unlaunched copy whose
        // build finished (see the WHY NOT block in CopyPlanStep), so it would
        // eject people from drafts they were still working on. `launched` is
        // the honest pairing — a stamped platform id OR a completed api step.
        //
        // It used to read platform_campaign_id alone, which is why a launched
        // Search/PMax copy still landed here: nothing stamps that id when a
        // copy's launch completes, so this guard passed on a campaign that was
        // already live and the user got the dead Push step. The api step is the
        // platform create itself, and for a planning copy it only runs once
        // planning-launch resumes the orchestration, so it cannot be true of a
        // draft that has never been pushed.
        if (review.launched || review.campaign?.platform_campaign_id) {
          navigate(`/campaigns/${buildRowId}`, { replace: true });
          return;
        }
        patchBuild(sourceId, {
          status: 'review',
          campaignRowId: buildRowId,
          launcherResult: review.launcherResult,
          reviewPlatform: review.platform,
          launcherResultPath: review.launcherResultPath,
          alreadyLaunched: review.alreadyLaunched,
          // Deliberately NO payloadKey. The step-1 inputs in this session did
          // not produce this plan, so stepping back to Describe and forward
          // again must rebuild (which soft-deletes this row via
          // startBuildFor's superseded cleanup) rather than silently adopt a
          // plan built from different inputs.
        });
      })
      .catch((err) => {
        if (cancelled) return;
        patchBuild(sourceId, {
          status: 'failed',
          error: err.response?.data?.error || err.message || 'Could not reload this copy',
        });
      });
    return () => { cancelled = true; };
  }, [searchParams, campaignIds, accountId, userId, patchBuild, navigate]);

  // Names already in the DESTINATION account — resolveGates needs them to
  // confirm a duplicate_name fix actually landed on a free name, and the
  // "use a unique name" quick-fix collide-checks against them.
  const [existingCampaignNames, setExistingCampaignNames] = useState([]);
  const loadExistingNames = useCallback(async () => {
    try {
      setExistingCampaignNames(await fetchExistingCampaignNames(platform, { accountId }));
    } catch (err) {
      // Best-effort: an empty list under-reports duplicates rather than
      // inventing them — the backend gate already flagged the real ones.
      console.error('Error fetching existing campaign names for gate resolution', err);
    }
  }, [platform, accountId]);

  // The POST /copy payload for one source campaign, fired at step-2 entry
  // with planningMode so the backend builds WITHOUT launching. `campaignId`
  // is the record's own platform_campaign_id — that column already holds the
  // comma-joined id set for a merged shell (the same value the old preview
  // echoed back via source_campaign_id_requested), and
  // campaign_copy_handler.py splits and imports each id itself. There is no
  // excludedIds field anymore: excluding children is a row DELETION in the
  // Confirm & adjust grid, applied to the built template through the
  // planning-review PUT.
  // The name this copy will be created under: what the user typed in step 1's Campaign
  // name field > the frozen pre-filled default > the old `<name> (copy)`. That last one
  // is unreachable in practice (the field is required and pre-filled) and is kept only so
  // a payload can never carry an empty campaign name.
  //
  // ONE definition, used by both the build payload and the Push step's changeset labels —
  // two copies of this precedence would eventually disagree about what is being launched.
  const intendedNameFor = useCallback(
    (c) => renameOverrides?.[c.id]?.trim() || nameDefaults?.[c.id] || `${c.name} (copy)`,
    [renameOverrides, nameDefaults],
  );

  const buildPayloadFor = useCallback((c) => {
    return {
      platform,
      campaignId: c.platform_campaign_id,
      // Pins the source row for the backend's TTD id-set expansion
      // (resolveTtdCopyCampaignIds): a multi-campaign TTD launch historically
      // stamped only its FIRST campaign id onto platform_campaign_id, so the
      // backend re-derives the full set from this row's orchestration api
      // result rather than trusting the stamped column.
      sourceCampaignRowId: c.id,
      ...(isGoogleAdsCopyPlatform(platform)
        ? { customerId: c.advertiser_id }
        : { advertiserId: c.advertiser_id }),
      // Precedence: what the user typed in step 1's Campaign name field > the frozen
      // pre-filled default > the old `<name> (copy)`. That last one is unreachable in
      // practice (the field is required and pre-filled) and is kept only so a payload can
      // never carry an empty campaign name.
      newName: intendedNameFor(c),
      startDate: formFields.startDate,
      endDate: formFields.endDate,
      budget: formFields.budget,
      budgetType: formFields.budgetType,
      folderId: c.folder_id || sourceCampaigns[0]?.folder_id,
      channelId: c.channel_id || sourceCampaigns[0]?.channel_id,
      prompt,
      accountName: c.account_name || sourceCampaigns[0]?.account_name,
      accountId,
      userId,
      // Interpret-step ops the user ACCEPTED (checkbox state in opOverrides
      // outranks the LLM's own applied flag), with each op's effective
      // applied value baked in. The backend applies ops that carry structured
      // field_changes deterministically to the built template
      // (copy_op_applier.py) — before its gates run, so e.g. a renaming op is
      // what the duplicate-name gate checks. Ops without field_changes remain
      // display-only, exactly as before.
      ...(interpretation?.ops?.length > 0 && {
        appliedOps: interpretation.ops.map((op) => ({
          ...op,
          applied: Object.prototype.hasOwnProperty.call(opOverrides, op.id)
            ? opOverrides[op.id] : op.applied,
        })),
      }),
      // DV360-only. Campaign-wide geo override; one location name per line. Emitted only for
      // dv360 and only when at least one name is present — the backend (buildCampaignCopyMessageData)
      // re-gates on platform === 'dv360', so it can never reach a PMax / Search / TTD copy.
      ...(platform === 'dv360' && (() => {
        const included = parseGeoLines(dv360Geo.included);
        const excluded = parseGeoLines(dv360Geo.excluded);
        return (included.length || excluded.length) ? { geoOverrides: { included, excluded } } : null;
      })()),
      // Google Ads (Search + PMax) geo override. Emitted for google_search / google_pmax
      // when at least one location is present. The backend re-gates per platform and
      // forwards { included }; it REPLACES the source campaign's positive geo. The copy
      // handler resolves every entry to a Google geo target at build time and pins it
      // (excluded not yet supported).
      ...((platform === 'google_search' || platform === 'google_pmax') && (() => {
        const included = geoOverrideIncluded(searchGeo);
        return included.length ? { geoOverrides: { included } } : null;
      })()),
      // DV360-only. Create the copy under a different advertiser than the source. Emitted only for
      // dv360 and only when non-blank; the backend re-gates on platform === 'dv360'.
      ...(platform === 'dv360' && targetAdvertiserId.trim()
        ? { targetAdvertiserId: targetAdvertiserId.trim() } : null),
      // GOOGLE ADS destination account. Sent only when the user UNCHECKED "copy to the
      // same advertiser" AND typed an id, so the default path is byte-identical to what
      // it sent before this field existed. The backend re-gates on the Google platforms
      // and on the id differing from the source, and the create step (fastTrackOrchestrator)
      // points the campaign-create at it; the IMPORT still reads from the source account.
      ...(isGoogleAdsCopyPlatform(platform) && !copyToSameAccount && targetCustomerId.trim()
        ? { targetCustomerId: targetCustomerId.trim() } : null),
      // Google Ads (Search + PMax) step-1 TARGET CPA, an amount in the account currency
      // (the copiers convert to micros). Blank is OMITTED rather than sent as '': Search
      // keeps source bidding, while PMax chooses plain Maximize Conversions when the
      // source had a target CPA. A stated value pins either copy to that CPA.
      ...(isGoogleAdsCopyPlatform(platform) && String(gadsOverrides.targetCpa || '').trim()
        ? { targetCpa: String(gadsOverrides.targetCpa).trim() } : null),
      // No urlOverrides from this flow any more — URL tracking is edited in step 2,
      // against the built template. The backend and both copiers still accept the
      // field, so API and CLI callers are unchanged; the UI simply stops sending it
      // and lets the build carry the source's tracking (converting a pure-tagging
      // template to a suffix when it would otherwise collide), for step 2 to adjust.
      planningMode: true,
    };
  }, [platform, intendedNameFor, formFields, prompt,
    sourceCampaigns, accountId, userId, interpretation, opOverrides, dv360Geo, searchGeo,
    targetAdvertiserId, gadsOverrides, copyToSameAccount, targetCustomerId]);

  // POST /copy (planningMode) for one campaign, then poll its messageId until
  // the backend poller flips it to 'review' (build landed) and fetch the
  // built template from planning-review. Same interval-poll-until-terminal
  // shape as the preview effect above.
  const startBuildFor = useCallback(async (c) => {
    const payload = buildPayloadFor(c);
    const payloadKey = JSON.stringify(payload);

    // A REBUILD supersedes the previous build's campaigns row (every POST
    // /copy creates a fresh one) — soft-delete the old draft so retries and
    // changed-inputs rebuilds don't leak a dead row into the campaign list
    // per attempt. Best-effort by design: the rebuild must never be blocked
    // by cleanup, so failures only log. Never fired at anything launched —
    // paranoia guard, since only failed/superseded-draft builds reach here.
    const superseded = buildsRef.current?.[c.id];
    if (superseded?.campaignRowId && !superseded.alreadyLaunched && !pushedRef.current) {
      deleteCampaignRow(superseded.campaignRowId, { accountId, userId })
        .catch((err) => console.error(
          `Could not delete superseded draft campaign row ${superseded.campaignRowId}`, err,
        ));
    }

    patchBuild(c.id, {
      status: 'building', error: null, saveError: null, launcherResult: null,
      campaignRowId: null, messageId: null, chatId: null, links: null,
      alreadyLaunched: false, payloadKey,
    });

    // Fail fast without a platform id or channel — the POST is a
    // guaranteed 400 without either.
    if (!payload.campaignId || !payload.channelId) {
      patchBuild(c.id, { status: 'failed', error: 'Missing platform campaign id or channel — cannot build' });
      return;
    }

    try {
      const res = await copyCampaign(payload);
      patchBuild(c.id, {
        campaignRowId: res.campaignId, messageId: res.messageId,
        chatId: res.chatId, links: res.links || null,
      });

      const timer = setInterval(async () => {
        try {
          const statusRes = await fetchMessageStatus(res.messageId, { accountId, userId });
          if (isBuildReady(statusRes.status)) {
            clearInterval(timer);
            try {
              const review = await fetchPlanningReview(res.campaignId, { accountId, userId });
              patchBuild(c.id, {
                status: 'review',
                launcherResult: review.launcherResult || null,
                reviewPlatform: review.platform || null,
                launcherResultPath: review.launcherResultPath || null,
                alreadyLaunched: Boolean(review.alreadyLaunched),
              });
            } catch (err) {
              patchBuild(c.id, {
                status: 'failed',
                error: err.response?.data?.error || err.message || 'Built, but the planning review could not be loaded',
              });
            }
          } else if (TERMINAL_MESSAGE_STATUSES.has(statusRes.status)) {
            clearInterval(timer);
            // Carry the failure REASON, not just the status — GET /message/
            // :id/status returns it in `content`, with the handler payload's
            // own `error` as fallback (same sourcing the push poll uses).
            patchBuild(c.id, {
              status: 'failed',
              error: statusRes.content || statusRes.result?.error || `Build ${statusRes.status}`,
            });
          }
        } catch (err) {
          // Transient poll error — leave status as-is, the next tick retries.
          console.error('Error polling copy-build message status', err);
        }
      }, BUILD_POLL_INTERVAL_MS);
      buildPollTimers.current.push(timer);
    } catch (err) {
      patchBuild(c.id, {
        status: 'failed',
        error: err.response?.data?.error || err.message || 'Failed to start the copy build',
      });
    }
  }, [buildPayloadFor, patchBuild, accountId, userId]);

  // Fired on step-2 (Adjust) entry. A campaign whose build is already
  // running or reviewed AND whose inputs haven't changed since (payloadKey)
  // is left alone, so stepping back and forth doesn't spawn duplicate
  // campaign rows; changed inputs or a failed build start a fresh one.
  const startBuilds = useCallback(() => {
    loadExistingNames();
    sourceCampaigns.forEach((c) => {
      const existing = builds[c.id];
      if (existing
        && (existing.status === 'building' || existing.status === 'review')
        && existing.payloadKey === JSON.stringify(buildPayloadFor(c))) return;
      startBuildFor(c);
    });
  }, [sourceCampaigns, builds, buildPayloadFor, startBuildFor, loadExistingNames]);

  // Effective (unresolved) gates per build: the backend rows off
  // launcherResult.gates, re-checked against the CURRENT launcherResult so a
  // blocker the user already fixed in the grid stops gating the push. Both
  // step 2 (display) and step 3 (push enablement) read THIS, never the raw
  // rows, so they can't disagree.
  const gatesByCampaign = useMemo(() => {
    const out = {};
    for (const [cid, b] of Object.entries(builds)) {
      if (b?.status !== 'review' || !b.launcherResult) { out[cid] = null; continue; }
      const buildPlatform = b.reviewPlatform || b.launcherResult.platform || b.launcherResult.Platform;
      out[cid] = resolveGates(b.launcherResult.gates, b.launcherResult, buildPlatform, existingCampaignNames);
    }
    return out;
  }, [builds, existingCampaignNames]);

  // PUT the edited launcherResult (WHOLE object — targeting_plan/
  // creative_plan/gates ride along untouched). The local state adopts the
  // edited object OPTIMISTICALLY, before the PUT resolves — so a second edit
  // committed while the first save is in flight applies on top of the first
  // instead of a stale base and can't clobber it. A failed PUT keeps the
  // optimistic state and reports saveError: because every PUT sends the
  // WHOLE current launcherResult, the next successful save persists
  // everything, so nothing is lost — only delayed. Gates re-resolve
  // automatically via the memo above.
  const saveBuildEdits = useCallback(async (campaignId, editedLauncherResult) => {
    const b = builds[campaignId];
    if (!b?.campaignRowId) return;
    patchBuild(campaignId, { launcherResult: editedLauncherResult, saving: true, saveError: null });
    try {
      await savePlanningReview(b.campaignRowId, editedLauncherResult, { accountId, userId });
      patchBuild(campaignId, { saving: false });
    } catch (err) {
      patchBuild(campaignId, {
        saving: false,
        saveError: err.response?.data?.error || err.message || 'Save failed',
      });
    }
  }, [builds, patchBuild, accountId, userId]);

  /**
   * Clear a duplicate_name blocker by renaming the copy to something free —
   * computed client-side (computeUniqueCopyRename, the pure tested core),
   * written through the SAME edit-apply-save path a manual grid rename takes
   * (savePlanningReview), so the persisted template and the grid can't
   * drift. renameOverrides is updated too, so a later REBUILD of this
   * campaign keeps the fixed name.
   *
   * NEVER a silent no-op (the original bug): the click either changes the
   * name in the grid or puts WHY it couldn't into the build's saveError
   * banner. The suggester's taken-set is seeded with the gate's own flagged
   * value inside computeUniqueCopyRename, so a stale/empty
   * existingCampaignNames (the fetch is async and races the build) can no
   * longer make the handler conclude "nothing collides" and bail.
   */
  const applyUniqueCampaignName = useCallback(async (campaignId) => {
    const b = builds[campaignId];
    const gate = (gatesByCampaign[campaignId]?.blocked || [])
      .find((g) => g.code === 'duplicate_name');
    // Collide-check against the account AND the other builds in this copy, so
    // renaming one of two identically-named copies can't just move the clash.
    const siblingNames = Object.entries(builds)
      .filter(([cid]) => String(cid) !== String(campaignId))
      .flatMap(([, other]) => campaignNamesFromLauncherResult(other?.launcherResult));
    const result = computeUniqueCopyRename({
      launcherResult: b?.launcherResult,
      gate,
      existingNames: existingCampaignNames,
      siblingNames,
    });
    if (result.error) {
      patchBuild(campaignId, { saveError: result.error });
      return;
    }
    setRenameOverrides((prev) => ({ ...prev, [campaignId]: result.newName }));
    await saveBuildEdits(campaignId, result.edited);
    // Updating renameOverrides changes what buildPayloadFor(c) produces, so
    // without re-stamping payloadKey a later step re-entry would flag this
    // build's inputs as "changed" and REBUILD it — deleting the renamed
    // draft and losing every other saved grid edit with it. The rename is
    // already applied to the saved template, so the key must say so.
    const c = sourceCampaigns.find((x) => String(x.id) === String(campaignId));
    if (c) {
      patchBuild(campaignId, {
        payloadKey: JSON.stringify({ ...buildPayloadFor(c), newName: result.newName }),
      });
    }
    // The saved rename also updated campaigns.name server-side (the
    // planning-review PUT syncs it for copy rows), so the account's name
    // list changed — refresh it so the NEXT gate resolution / quick-fix
    // reasons about current reality instead of the pre-rename snapshot.
    loadExistingNames();
  }, [builds, gatesByCampaign, existingCampaignNames, saveBuildEdits, patchBuild,
    loadExistingNames, sourceCampaigns, buildPayloadFor]);

  // Manual retry for a failed build (re-POSTs /copy for that one campaign).
  const retryBuild = useCallback((campaignId) => {
    const c = sourceCampaigns.find((x) => String(x.id) === String(campaignId));
    if (c) startBuildFor(c);
  }, [sourceCampaigns, startBuildFor]);

  // ── Step 3 — push ─────────────────────────────────────────────────────────
  const [pushed, setPushed] = useState(false);
  useEffect(() => { pushedRef.current = pushed; }, [pushed]);

  // Once pushed there is nothing left to resume — the copy is a real campaign
  // now. Drop the draft so starting another copy of the same source doesn't
  // inherit the last one's description and dates.
  useEffect(() => {
    if (!pushed || !draftKey) return;
    try {
      sessionStorage.removeItem(draftKey);
    } catch (err) {
      console.warn('Could not clear the copy draft', err);
    }
  }, [pushed, draftKey]);

  // ── Leave guard ───────────────────────────────────────────────────────────
  // "Edited" = the user has typed something into step 1, or has moved past it
  // into a plan being built/adjusted. None of that is a saved campaign until
  // the push succeeds, so `pushed` clears the flag — after a push there is
  // nothing left to lose, and the push step navigates to the new campaign
  // itself. The default (empty form, still on Describe) is NOT dirty, so
  // opening the flow and backing straight out never prompts.
  const hasUnsavedEdits = !pushed && (
    step !== 'describe'
    || prompt.trim() !== ''
    || Boolean(formFields.startDate) || Boolean(formFields.endDate) || Boolean(formFields.budget)
    || formFields.budgetType !== 'daily'
    || dv360Geo.included.trim() !== '' || dv360Geo.excluded.trim() !== ''
    || geoOverrideIncluded(searchGeo).length > 0
    || targetAdvertiserId.trim() !== ''
    || copyToSameAccount === false || targetCustomerId.trim() !== ''
    || Object.values(gadsOverrides).some((v) => String(v || '').trim() !== '')
    || Object.keys(renameOverrides).length > 0
    || Object.keys(opOverrides).length > 0
  );
  // WHAT the user loses differs by where they are, so the warning has to say
  // the true thing for that spot:
  //   • Set up — typed input that was never sent anywhere.
  //   • Confirm & adjust / Push — a BUILT plan. Every committed grid edit is
  //     already persisted to the template, so "unsaved" would be wrong; what
  //     dies is the unlaunched build, which the flow offers no way back into.
  //   • …with an edit still saving, or one whose save failed — that edit is
  //     genuinely unsaved, which outranks both of the above.
  const anyEditUnsaved = Object.values(builds)
    .some((b) => b?.saving || b?.saveError);
  const leaveMessage = copyLeaveMessage(step, anyEditUnsaved);
  // True = safe to navigate. Guards the in-page exits below; reload, tab close,
  // browser Back/Forward and the sidebar's nav are handled inside the hook.
  const confirmLeave = useUnsavedChangesGuard(hasUnsavedEdits, leaveMessage);

  const goBack = () => setStep((s) => {
    const i = activeSteps.indexOf(s);
    return activeSteps[Math.max(0, (i < 0 ? 0 : i) - 1)];
  });
  // Leaving Describe fires the planning-mode builds immediately — the
  // Confirm & adjust step IS the build's review surface, and nothing sits
  // between them.
  // Leaving step 1 now CHECKS THE NAME against the account first.
  //
  // The creators used to append a uuid suffix ("... - a54edf") so two launches of one
  // template could never collide on Google's account-unique campaign name. Copies now
  // set preserve_campaign_name and reach Google exactly as typed, which means this flow
  // owns the uniqueness that suffix used to buy blindly -- and has to check it BEFORE
  // building, because a collision found at launch has already created a campaigns row,
  // an orchestration and a built template to unpick.
  //
  // Compared through normalizeCampaignNameForCompare, which mirrors copy_gates.py's
  // _norm_name exactly, so a name this call accepts is one the Python gate also accepts.
  // The existing duplicate-name GATE stays where it is: it catches a name that became
  // taken between this check and the launch, which no pre-check can rule out.
  const [nameCheck, setNameCheck] = useState({ checking: false, error: null });

  const goToAdjust = async () => {
    setNameCheck({ checking: true, error: null });
    let existing = [];
    try {
      existing = await fetchExistingCampaignNames(platform, { accountId });
    } catch (err) {
      // The lookup is a courtesy, not a gate: failing it must not strand a copy the
      // user is entitled to make. The duplicate-name gate still stands behind this,
      // and Google itself refuses a true collision.
      console.warn('Could not check the campaign name for uniqueness — continuing', err);
      setNameCheck({ checking: false, error: null });
      setStep('plan');
      startBuilds();
      return;
    }

    const taken = new Set(existing.map(normalizeCampaignNameForCompare));
    const collisions = sourceCampaigns
      .map((c) => intendedNameFor(c))
      .filter((name) => taken.has(normalizeCampaignNameForCompare(name)));
    if (collisions.length > 0) {
      // Stay on step 1 with the Campaign name field focused and the reason stated —
      // the box to fix it in is the one already on screen.
      setNameCheck({
        checking: false,
        error: `"${collisions[0]}" is already used by a campaign in this account. `
          + 'Google Ads needs a unique campaign name — enter a different one to continue.',
      });
      return;
    }

    setNameCheck({ checking: false, error: null });
    setStep('plan');
    startBuilds();
  };

  // A step key the sequence doesn't hold can only come from stale state (an
  // old session, a hand-edited value) — the `step` memo already resolves those
  // to 'describe'. What still needs catching is a step that IS valid but has
  // nothing behind it: ?step=push on a shared link, or a session whose build
  // reference was lost. Both steps past Describe render a build; without one
  // they'd show an empty review surface with a dead Push button, so start over.
  //
  // Runs ONCE, on entry. It deliberately does not keep watching: mid-flow
  // there is always a moment — between goToAdjust's setStep('plan') and the
  // first build appearing — where a live check would see an empty `builds` and
  // throw the user back to step 1. Deep-linking is the only case that needs
  // catching, and it is decided the instant the component mounts.
  const entryGuardRef = useRef(false);
  useEffect(() => {
    if (entryGuardRef.current) return;
    entryGuardRef.current = true;
    if (step === 'describe' || searchParams.get('build')) return;
    setStep('describe');
  }, [step, searchParams, setStep]);
  const goToPush = () => setStep('push');

  const campaignName = sourceCampaigns[0]?.name ? `Copy of ${sourceCampaigns[0].name}` : 'Copy campaign';

  return (
    <div className="copy-workflow" data-theme="deep-navy">
      <div className="cw-main">
        <div className="cw-topbar">
          <div className="tb-bc">
            <a href="/campaigns" onClick={(e) => { e.preventDefault(); if (confirmLeave()) navigate('/campaigns'); }}>Campaigns</a>
            <span className="material-icons" style={{ fontSize: 13, color: '#d1d0ce' }}>chevron_right</span>
            <strong>Copy campaign</strong>
            {step !== 'describe' && <>
              <span className="material-icons" style={{ fontSize: 13, color: '#d1d0ce' }}>chevron_right</span>
              <strong>{campaignName}</strong>
            </>}
          </div>
          <span className="tb-badge" style={pushed ? { background: '#f4ffe7', color: '#3b6f3f', borderColor: '#bde986' } : undefined}>
            <span className="material-icons" style={{ fontSize: 10 }}>{pushed ? 'check_circle' : 'edit_note'}</span> {pushed ? 'Pushed' : 'Draft — not pushed'}
          </span>
          {/* Only meaningful once campaigns are actually loaded into the
              workflow (past the entry picker) — before that, step is
              already 1 and there's nothing described yet to re-describe. */}
          {SHOW_COPY_INTERPRET_UI && campaignIds.length > 0 && (
            <div className="tb-right">
              <button
                type="button"
                className="tb-link"
                style={{ background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}
                onClick={() => setStep('describe')}
              ><span className="material-icons" style={{ fontSize: 13 }}>auto_awesome</span> Re-describe</button>
            </div>
          )}
        </div>

        {campaignIds.length === 0 ? (
          <CopyCampaignEntryPicker
            accountId={accountId}
            userId={userId}
            onCancel={() => { if (confirmLeave()) navigate('/campaigns'); }}
            // Feeds the chosen ids into the SAME ?campaignIds= URL param the
            // rest of this component (and everything below it) is already
            // driven off of — see this file's own top doc comment. Does NOT
            // touch `step`, which is still at its untouched initial value of
            // 1, so the flow naturally continues into CopyDescribeStep once
            // campaignIds.length > 0 on the next render.
            onContinue={(ids) => setSearchParams({ campaignIds: ids.join(',') })}
          />
        ) : (
          <>
            {step === 'describe' && (
              <CopyDescribeStep
                sourceCampaigns={sourceCampaigns}
                sourcesLoading={sourcesLoading}
                sourcesError={sourcesError}
                platform={platform}
                prompt={prompt}
                setPrompt={setPrompt}
                interpretation={interpretation}
                interpreting={interpreting}
                onReRead={handleReRead}
                intent={intent}
                setIntent={setIntent}
                opOverrides={opOverrides}
                setOpOverrides={setOpOverrides}
                renameOverrides={renameOverrides}
                setRenameOverrides={setRenameOverrides}
                nameDefaults={nameDefaults}
                nameError={nameCheck.error}
                nameChecking={nameCheck.checking}
                onNameEdited={() => setNameCheck((prev) => (
                  prev.error ? { checking: false, error: null } : prev))}
                formFields={formFields}
                setFormFields={setFormFields}
                dv360Geo={dv360Geo}
                setDv360Geo={setDv360Geo}
                searchGeo={searchGeo}
                setSearchGeo={setSearchGeo}
                accountId={accountId}
                userId={userId}
                targetAdvertiserId={targetAdvertiserId}
                setTargetAdvertiserId={setTargetAdvertiserId}
                copyToSameAccount={copyToSameAccount}
                setCopyToSameAccount={setCopyToSameAccount}
                targetCustomerId={targetCustomerId}
                setTargetCustomerId={setTargetCustomerId}
                gadsOverrides={gadsOverrides}
                setGadsOverrides={setGadsOverrides}
                manualOpen={manualOpen}
                setManualOpen={setManualOpen}
                onBack={() => { if (confirmLeave()) navigate('/campaigns'); }}
                onContinue={goToAdjust}
                stepLabels={stepLabels}
              />
            )}
            {step === 'plan' && (
              <CopyPlanStep
                stepLabels={stepLabels}
                accountId={accountId}
                userId={userId}
                platform={platform}
                sourceCampaigns={sourceCampaigns}
                builds={builds}
                // Resolved per-build gates (backend rows re-checked against
                // the CURRENT launcherResult) — step 3 reads the same memo,
                // so what this step shows cleared IS what unblocks the push.
                gatesByCampaign={gatesByCampaign}
                onApplyUniqueName={applyUniqueCampaignName}
                onSaveBuild={saveBuildEdits}
                onRetryBuild={retryBuild}
                campaignName={campaignName}
                // The COPY's own push state — the only honest "is this live yet"
                // signal on this step. See the readOnly note in CopyPlanStep.
                pushed={pushed}
                onBack={goBack}
                onContinue={goToPush}
              />
            )}
            {step === 'push' && (
              <CopyPushStep
                stepLabels={stepLabels}
                platform={platform}
                sourceCampaigns={sourceCampaigns}
                builds={builds}
                gatesByCampaign={gatesByCampaign}
                onApplyUniqueName={applyUniqueCampaignName}
                intendedNameFor={intendedNameFor}
                interpretation={interpretation}
                opOverrides={opOverrides}
                formFields={formFields}
                accountId={accountId}
                userId={userId}
                // WHERE the copy lands on the platform. For DV360 that is the
                // step-1 target-advertiser override when one was given — the
                // copy is created under it, so showing the source's advertiser
                // there would name the account the copy is leaving.
                destinationAdvertiserId={
                  (platform === 'dv360' && targetAdvertiserId.trim())
                  || (isGoogleAdsCopyPlatform(platform) && !copyToSameAccount
                    && targetCustomerId.trim())
                  || sourceCampaigns[0]?.advertiser_id
                }
                onBack={goBack}
                onPushComplete={() => setPushed(true)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
